import { fetchTokenList } from '@1delta/data-sdk'
import { apyToAprPercent } from '../../../utils/parsing'
import { YieldFetcher } from '../../types'

/**
 * Accountable Capital — RWA / private-credit lending loans.
 *
 * The listing endpoint returns every loan with its current APY and the
 * symbol of its share (receipt) token (`shares_symbol`). We don't hard-
 * code per-loan keys: instead we resolve each loan's share token to its
 * `assetGroup` in the 1delta token list for the loan's chain (the same
 * `<group>::<symbol>` string the rest of the yield pipeline keys on),
 * and emit a yield only for loans whose share token is actually tracked.
 * New loans surface automatically once their token is added to the list.
 *
 *   list:   https://yield.accountable.capital/api/loan      → { items: [...] }
 */
const ACCOUNTABLE_LIST_URL = 'https://yield.accountable.capital/api/loan'

interface AccountableLoan {
  chain_id: number | null
  shares_symbol: string | null
  net_apy: number | null
  apy_7d: number | null
}

/** Build a `symbol(lower) → assetGroup` map for one chain's token list.
 *  Symbols that resolve to more than one distinct `assetGroup` are
 *  dropped (ambiguous — we can't safely attribute a yield to them). */
async function symbolToAssetGroup(
  chainId: string,
): Promise<Map<string, string>> {
  const list = await fetchTokenList(chainId)
  const bySymbol = new Map<string, string>()
  const ambiguous = new Set<string>()
  for (const t of Object.values(list)) {
    const symbol = t?.symbol?.toLowerCase()
    const group = t?.assetGroup
    if (!symbol || !group) continue
    const existing = bySymbol.get(symbol)
    if (existing && existing !== group) {
      ambiguous.add(symbol)
    } else {
      bySymbol.set(symbol, group)
    }
  }
  for (const s of ambiguous) bySymbol.delete(s)
  return bySymbol
}

export const accountableFetcher: YieldFetcher = {
  label: 'ACCOUNTABLE',
  fetch: async () => {
    const res = await fetch(ACCOUNTABLE_LIST_URL, {
      headers: { Accept: 'application/json' },
    }).then((r) => r.json())
    const loans: AccountableLoan[] = res?.items ?? []

    // Resolve token lists once per chain that has loans.
    const chains = Array.from(
      new Set(
        loans
          .map((l) => l?.chain_id)
          .filter((c): c is number => typeof c === 'number')
          .map(String),
      ),
    )
    const maps = new Map<string, Map<string, string>>()
    await Promise.all(
      chains.map(async (chainId) => {
        // A missing/unreachable list just means none of that chain's loans
        // resolve — skip them rather than failing the whole fetch.
        const map = await symbolToAssetGroup(chainId).catch(
          () => new Map<string, string>(),
        )
        maps.set(chainId, map)
      }),
    )

    const out: { [asset: string]: number } = {}
    for (const loan of loans) {
      const chainId = loan?.chain_id != null ? String(loan.chain_id) : undefined
      const symbol = loan?.shares_symbol?.toLowerCase()
      if (!chainId || !symbol) continue
      const assetGroup = maps.get(chainId)?.get(symbol)
      if (!assetGroup) continue
      const apy = Number(loan.net_apy ?? loan.apy_7d ?? 0)
      if (!Number.isFinite(apy)) continue
      out[assetGroup] = apyToAprPercent(apy)
    }
    return out
  },
}
